import React, { useState } from 'react';
import { Modal, Form, Input, Select, Radio, message, Button, Upload, InputNumber, Checkbox } from 'antd';
import { PlusOutlined, MinusCircleOutlined, FullscreenOutlined, UploadOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';

// 自定义样式
const customStyles = `
  .custom-textarea .ant-input-data-count {
    color: #1890ff !important;
    font-weight: 600 !important;
    background: rgba(24, 144, 255, 0.1) !important;
    padding: 2px 6px !important;
    border-radius: 4px !important;
    border: 1px solid #1890ff !important;
  }
  
  .custom-textarea .ant-input-data-count.warning {
    color: #fa8c16 !important;
    background: rgba(250, 140, 22, 0.1) !important;
    border-color: #fa8c16 !important;
  }
  
  .custom-textarea .ant-input-data-count.danger {
    color: #ff4d4f !important;
    background: rgba(255, 77, 79, 0.1) !important;
    border-color: #ff4d4f !important;
  }
  
  .product-select .ant-select-selector {
    min-height: 24px !important;
    max-height: 50px !important;
    overflow-y: auto !important;
  }
  
  .product-select .ant-select-selection-item {
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// 注入样式
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('custom-textarea-styles');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'custom-textarea-styles';
    style.textContent = customStyles;
    document.head.appendChild(style);
  }
}

const { Option } = Select;
const { TextArea } = Input;

const MAPolicyForm = ({ visible, editingRecord, onCancel, onSuccess, readOnlyMode = false, isCityMode = false }) => {
  const [form] = Form.useForm();
  const [fullscreenModal, setFullscreenModal] = useState({ visible: false, value: '', fieldPath: null });
  const [hasSavedDraft, setHasSavedDraft] = useState(false); // 跟踪是否已暂存
  const [provincePolicyTemplates, setProvincePolicyTemplates] = useState([]);
  const { user } = useAuth(); // 获取当前用户信息
  const navigate = useNavigate(); // 添加导航钩子
  const DRAFT_KEY = 'ma_policy_draft';

  // 省级政策专属编辑权限配置
  const PROVINCE_EDITOR_MAP = {
    '上海': 'zhouyilu',
    '浙江省': 'puyi',
    '江苏省': 'wubing',
    '广东省': 'huangpenghao'
  };

  const CITY_TEMPLATE_FIELDS = [
    'hcv_pi_q4_2025_quantity_ratio', 'hcv_pi_q4_2025_amount_ratio',
    'hcv_pi_q1_2026_quantity_ratio', 'hcv_pi_q1_2026_amount_ratio',
    'hcv_pi_q2_2026_quantity_ratio', 'hcv_pi_q2_2026_amount_ratio',
    'hcv_pi_q3_2026_quantity_ratio', 'hcv_pi_q3_2026_amount_ratio',
    'hcv_pi_q4_2026_quantity_ratio', 'hcv_pi_q4_2026_amount_ratio',
    'cns_q4_2025_quantity_ratio', 'cns_q4_2025_amount_ratio',
    'cns_q1_2026_quantity_ratio', 'cns_q1_2026_amount_ratio',
    'cns_q2_2026_quantity_ratio', 'cns_q2_2026_amount_ratio',
    'cns_q3_2026_quantity_ratio', 'cns_q3_2026_amount_ratio',
    'cns_q4_2026_quantity_ratio', 'cns_q4_2026_amount_ratio',
    'marked_products',
    'price_policy_2', 'price_policy_3', 'price_policy_4', 'price_policy_5',
    'amount_ratio_red', 'amount_ratio_red_yellow',
    'in_key_monitoring_list'
  ];
  
  // 用于联动的状态
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [hasPolicyDocument, setHasPolicyDocument] = useState(null); // 用于控制"是否有官方明确澄清（不考核分子式）"的启用状态
  const [markedRedProducts, setMarkedRedProducts] = useState([]); // 标红产品已选项
  const [unmarkedYellowProducts, setUnmarkedYellowProducts] = useState([]); // 未被标黄产品已选项

  // VBP季度数据复制映射关系
  const VBP_QUARTER_COPY_MAP = [
    { from: 'q4_2025', to: 'q1_2026', label: '25年Q4 → 26年Q1' },
    { from: 'q1_2026', to: 'q2_2026', label: '26年Q1 → 26年Q2' },
    { from: 'q2_2026', to: 'q3_2026', label: '26年Q2 → 26年Q3' },
    { from: 'q3_2026', to: 'q4_2026', label: '26年Q3 → 26年Q4' }
  ];

  // 一键复制季度数据
  const copyQuarterData = (fromQuarter, toQuarter) => {
    const fieldPairs = [
      { prefix: 'hcv_pi', suffix: 'quantity_ratio' },
      { prefix: 'hcv_pi', suffix: 'amount_ratio' },
      { prefix: 'cns', suffix: 'quantity_ratio' },
      { prefix: 'cns', suffix: 'amount_ratio' }
    ];

    const updates = {};
    fieldPairs.forEach(({ prefix, suffix }) => {
      const fromField = `${prefix}_${fromQuarter}_${suffix}`;
      const toField = `${prefix}_${toQuarter}_${suffix}`;
      const value = form.getFieldValue(fromField);
      if (value) {
        updates[toField] = value;
      }
    });

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
      message.success(`已将 ${fromQuarter.replace('_', '年').replace('q', 'Q')} 的数据复制到 ${toQuarter.replace('_', '年').replace('q', 'Q')}`);
    } else {
      message.warning(`${fromQuarter.replace('_', '年').replace('q', 'Q')} 没有填写数据`);
    }
  };

  const productOptions = [
    '立普妥', '络活喜', '西乐葆', '乐瑞卡', '左洛复', '怡诺思'
  ];

  const rmaScoreOptions = [
    { value: '1', label: '1.公司产品获益，且患者可及性增加' },
    { value: '2', label: '2.相关领域产品获益，或患者可及性增加' },
    { value: '3', label: '3.无新增相关政策' },
    { value: '4', label: '4.公司产品影响，或患者可及性下降（如省级重点监控目录等）' }
  ];

  // 加载草稿或编辑数据
  React.useEffect(() => {
    if (visible) {
      if (editingRecord) {
        // 编辑模式：加载现有数据
        console.log('=== 编辑模式数据加载 ===');
        console.log('完整编辑数据:', JSON.stringify(editingRecord, null, 2));
        
        // 特别检查附件数据
        if (editingRecord.policy_cases) {
          editingRecord.policy_cases.forEach((policyCase, idx) => {
            console.log(`政策案例${idx + 1}附件数据:`, policyCase.attachments);
            if (policyCase.attachments && Array.isArray(policyCase.attachments)) {
              policyCase.attachments.forEach((att, attIdx) => {
                console.log(`  附件${attIdx + 1}:`, {
                  uid: att.uid,
                  name: att.name,
                  filename: att.filename,
                  path: att.path,
                  url: att.url,
                  status: att.status
                });
              });
            }
          });
        }
        if (editingRecord.payment_methods) {
          editingRecord.payment_methods.forEach((paymentMethod, idx) => {
            console.log(`支付方式${idx + 1}附件数据:`, paymentMethod.attachments);
          });
        }
        if (editingRecord.external_markets) {
          editingRecord.external_markets.forEach((externalMarket, idx) => {
            console.log(`院外市场${idx + 1}附件数据:`, externalMarket.attachments);
          });
        }
        
        form.setFieldsValue(editingRecord);
        setHasSavedDraft(false); // 编辑模式不需要草稿状态
        // 设置联动的state值
        if (editingRecord.division) setSelectedDivision(editingRecord.division);
        if (editingRecord.province) setSelectedProvince(editingRecord.province);
        if (editingRecord.has_policy_document) setHasPolicyDocument(editingRecord.has_policy_document);
        if (editingRecord.marked_red_products) setMarkedRedProducts(editingRecord.marked_red_products || []);
        if (editingRecord.unmarked_yellow_products) setUnmarkedYellowProducts(editingRecord.unmarked_yellow_products || []);
        // 只有编辑者需要看到加载编辑数据的提示（涉及暂存功能）
        if (user.role === 'editor' || user.role === 'city_editor') {
          message.info('已加载编辑数据');
        }
        console.log('=== 编辑模式数据加载完成 ===');
      } else {
        // 新增模式：尝试加载草稿
        const draft = localStorage.getItem(DRAFT_KEY);
        if (draft) {
          try {
            const draftData = JSON.parse(draft);
            form.setFieldsValue(draftData);
            setHasSavedDraft(true); // 加载草稿时标记为已暂存
            message.info('已加载暂存的草稿');
          } catch (error) {
            console.error('加载草稿失败:', error);
          }
        } else {
          setHasSavedDraft(false); // 没有草稿时重置状态
        }
      }
    }
  }, [visible, editingRecord, form]);

  React.useEffect(() => {
    if (!visible) return;

    const fetchProvincePolicyTemplates = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/ma-policy', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const provinceRecords = (response.data || []).filter(item => !item.responsible_city);
        setProvincePolicyTemplates(provinceRecords);
      } catch (error) {
        console.error('获取省级政策情况模板失败:', error);
      }
    };

    fetchProvincePolicyTemplates();
  }, [visible]);

  // 保存草稿（仅在新增模式下可用）
  const handleSaveDraft = () => {
    if (editingRecord) {
      message.warning('编辑模式下不支持暂存功能');
      return;
    }
    const values = form.getFieldsValue();
    localStorage.setItem(DRAFT_KEY, JSON.stringify(values));
    setHasSavedDraft(true); // 标记为已暂存
    message.success('草稿已暂存');
  };

  // 清除草稿
  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
  };

  const cloneTemplateValue = (value) => {
    if (Array.isArray(value)) {
      return JSON.parse(JSON.stringify(value));
    }

    if (value && typeof value === 'object') {
      return JSON.parse(JSON.stringify(value));
    }

    return value;
  };

  const findLatestProvinceTemplate = (province) => {
    return provincePolicyTemplates
      .filter(item => item.province === province && !item.responsible_city)
      .sort((a, b) => {
        const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
        const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
        return bTime - aTime;
      })[0];
  };

  const applyProvinceTemplateToCityForm = (templateRecord) => {
    const templateValues = {};

    CITY_TEMPLATE_FIELDS.forEach(field => {
      templateValues[field] = cloneTemplateValue(templateRecord[field]);
    });

    form.setFieldsValue(templateValues);
    message.success('已将该省份省级政策情况表中的VBP全流程管理和红黄标管控政策自动填入');
  };

  const handleProvinceChange = (value) => {
    setSelectedProvince(value);
    form.setFieldsValue({ responsible_city: undefined });

    // 检查是否是省级政策（没有城市）且是受限省份
    const isProvincialPolicy = !isCityMode && !editingRecord;
    const isRestrictedProvince = PROVINCE_EDITOR_MAP.hasOwnProperty(value);
    
    // 如果是新增省级政策，且省份受限，且用户不是指定的编辑者或不是管理员/超级管理员
    if (isProvincialPolicy && isRestrictedProvince) {
      const allowedEditor = PROVINCE_EDITOR_MAP[value];
      const isAuthorized = user && (
        user.username === allowedEditor || 
        ['admin', 'super_admin'].includes(user.role)
      );
      
      if (!isAuthorized) {
        Modal.warning({
          title: '无权填写此省级政策',
          content: `${value}的省级政策情况只能由指定的编辑者填写。如有需要，请联系管理员。`,
          onOk: () => {
            // 清空省份选择
            form.setFieldsValue({ province: undefined });
            setSelectedProvince(null);
          }
        });
        return;
      }
    }

    // 城市模式：提示使用省级模板
    const shouldPromptForTemplate = isCityMode && !editingRecord && value;
    if (!shouldPromptForTemplate) {
      return;
    }

    const latestTemplate = findLatestProvinceTemplate(value);
    if (!latestTemplate) {
      return;
    }

    Modal.confirm({
      title: '发现历史省级政策情况表',
      content: `检测到 ${value} 已存在省级政策情况表，是否将其中的VBP全流程管理和红黄标管控政策自动填入当前主责城市政策情况表？`,
      okText: '是',
      cancelText: '否',
      onOk: () => {
        applyProvinceTemplateToCityForm(latestTemplate);
      }
    });
  };

  // 渲染RMA赋分字段
  const renderRmaScoreField = (field, fieldName) => {
    // 管理员、超级管理员和delegate_visitor可以编辑RMA赋分
    const isDelegateVisitor = user && user.role === 'visitor' && user.delegate_editor_id;
    const canEditRmaScore = user && (['admin', 'super_admin'].includes(user.role) || isDelegateVisitor);
    
    if (canEditRmaScore) {
      return (
        <Form.Item {...field} name={fieldName} style={{ margin: 0 }}>
          <Select size="small" placeholder="请选择RMA赋分">
            {rmaScoreOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      );
    } else {
      // 编辑者只能查看，不能编辑
      return (
        <Form.Item {...field} name={fieldName} style={{ margin: 0 }}>
          <Input 
            disabled 
            size="small" 
            placeholder="RMA赋分" 
            style={readOnlyInputStyle}
          />
        </Form.Item>
      );
    }
  };

  // 检查字段是否只读
  const isFieldReadOnly = (fieldType = 'normal') => {
    // 如果传入了readOnlyMode参数，直接返回true
    if (readOnlyMode) return true;
    
    if (!user) return false;
    
    // RMA赋分字段的权限单独处理：admin、super_admin和delegate_visitor可以修改
    if (fieldType === 'rma_score') {
      const isDelegateVisitor = user.role === 'visitor' && user.delegate_editor_id;
      return !['admin', 'super_admin'].includes(user.role) && !isDelegateVisitor;
    }
    
    // MA姓名字段的权限单独处理：admin和super_admin可以修改
    if (fieldType === 'ma_name') {
      return !['admin', 'super_admin'].includes(user.role);
    }
    
    // 超级管理员可以编辑所有数据
    if (user.role === 'super_admin') {
      return false;
    }
    
    // 管理员(RMA)权限判断：
    // 1. 如果admin是该记录的负责MA（ma_name与用户名匹配），则拥有完整编辑权限
    // 2. 否则只能查看和审批，不能编辑内容（除了MA姓名和RMA赋分）
    if (user.role === 'admin') {
      if (editingRecord && editingRecord.ma_name === user.name) {
        // admin是该记录的负责MA，拥有完整编辑权限
        return false;
      }
      // 不是负责MA，只能查看和审批
      return true; // 所有其他字段都只读
    }
    
    // 编辑者(MA)只能编辑自己负责的省份/城市记录
    if (['editor', 'city_editor'].includes(user.role)) {
      // 如果是编辑模式，检查ma_name是否匹配
      if (editingRecord) {
        // 检查ma_name是否与当前用户名匹配
        return editingRecord.ma_name !== user.name;
      }
      // 新增模式，可以编辑
      return false;
    }
    
    // 代理访问者可以编辑被代理编辑者的数据
    if (user.role === 'visitor' && user.delegate_editor_id) {
      if (editingRecord) {
        return editingRecord.created_by !== user.delegate_editor_id;
      }
      return false;
    }
    
    return true; // 其他情况只读
  };

  // 临时调试函数
  const debugAttachmentData = () => {
    console.log('=== 调试附件数据 ===');
    console.log('当前表单数据:', form.getFieldsValue());
    
    const policyCase = form.getFieldValue(['policy_cases', 0]);
    if (policyCase) {
      console.log('第一个政策案例:', policyCase);
      console.log('第一个政策案例的附件:', policyCase.attachments);
      
      // 详细显示附件数组内容
      if (policyCase.attachments && Array.isArray(policyCase.attachments)) {
        console.log('附件数组长度:', policyCase.attachments.length);
        policyCase.attachments.forEach((attachment, index) => {
          console.log(`附件${index + 1}详细信息:`, JSON.stringify(attachment, null, 2));
        });
      }
    }
    console.log('=== 调试完成 ===');
  };

  // 渲染下拉菜单中的全选功能
  const renderSelectAllDropdown = (menu, fieldPath) => {
    const currentValue = form.getFieldValue(fieldPath) || [];
    const allSelected = currentValue.length === productOptions.length;
    
    return (
      <>
        <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <Button
            type="link"
            size="small"
            style={{ padding: '0 8px' }}
            onClick={() => {
              if (allSelected) {
                form.setFieldValue(fieldPath, []);
              } else {
                form.setFieldValue(fieldPath, [...productOptions]);
              }
            }}
          >
            {allSelected ? '取消全选' : '全选'}
          </Button>
        </div>
        {menu}
      </>
    );
  };

  // 渲染附件预览组件（所有用户都可以预览）
  const renderAttachmentPreview = (fileList) => {
    console.log('=== renderAttachmentPreview ===');
    console.log('fileList:', fileList);
    
    if (!fileList || fileList.length === 0) {
      return <span style={{ color: '#999', fontSize: '12px' }}>无附件</span>;
    }
    
    return (
      <div style={{ fontSize: '12px' }}>
        {fileList.map((file, index) => {
          console.log(`渲染附件 ${index}:`, {
            uid: file.uid,
            name: file.name,
            filename: file.filename,
            path: file.path,
            url: file.url,
            hasResponse: !!file.response,
            response: file.response
          });
          
          // 处理标准的Upload组件数据格式
          let fileName = '未知文件';
          let fileUrl = null;
          
          if (file && file.response && file.response.file) {
            // 新上传的文件
            fileName = file.response.file.originalname || file.name || `文件${index + 1}`;
            fileUrl = file.response.file.path;
            console.log(`附件 ${index} - 新上传文件:`, { fileName, fileUrl });
          } else if (file && file.name) {
            // 从数据库加载的文件
            fileName = file.name;
            fileUrl = file.path || file.url;
            console.log(`附件 ${index} - 数据库文件:`, { fileName, fileUrl });
          }
          
          if (fileUrl) {
            // 确保URL格式正确
            // 如果是完整URL（http/https开头），直接使用
            // 如果是相对路径，确保以 / 开头（相对于网站根目录）
            let fullUrl;
            if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
              fullUrl = fileUrl;
            } else if (fileUrl.startsWith('/')) {
              // 已经是根路径，对中文进行编码
              const urlParts = fileUrl.split('/');
              const encodedParts = urlParts.map(part => encodeURIComponent(part));
              fullUrl = encodedParts.join('/');
            } else {
              // 不是根路径，添加 / 前缀并编码
              const urlParts = fileUrl.split('/');
              const encodedParts = urlParts.map(part => encodeURIComponent(part));
              fullUrl = '/' + encodedParts.join('/');
            }
            console.log(`附件 ${index} - 最终URL:`, fullUrl);
            
            return (
              <div key={file.uid || index} style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <a 
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#1890ff', 
                    textDecoration: 'none',
                    fontSize: '12px',
                    flex: 1
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  onClick={() => console.log('点击附件链接:', fullUrl)}
                >
                  📎 {fileName}
                </a>
                <Button
                  type="link"
                  size="small"
                  icon={<FullscreenOutlined />}
                  style={{ padding: '0 4px', fontSize: '12px' }}
                  onClick={() => {
                    console.log('全屏查看附件:', fullUrl);
                    setFullscreenModal({
                      visible: true,
                      value: fullUrl,
                      fieldPath: `附件: ${fileName}`,
                      isAttachment: true
                    });
                  }}
                  title="全屏查看"
                />
              </div>
            );
          } else {
            return (
              <div key={file.uid || index} style={{ marginBottom: '4px', color: '#999', fontSize: '12px' }}>
                📎 {fileName} (无法下载)
              </div>
            );
          }
        })}
      </div>
    );
  };

  // 渲染附件上传字段
  const renderAttachmentField = (field, fieldName) => {
    const isReadOnly = isFieldReadOnly();
    
    if (isReadOnly) {
      // 只读模式：只显示预览
      return (
        <Form.Item {...field} name={fieldName} style={{ margin: 0 }}>
          <Form.Item noStyle shouldUpdate>
            {() => {
              let fileList = [];
              
              // 直接从表单的完整数据中获取
              try {
                const allFormData = form.getFieldsValue();
                
                // 根据字段名确定数据位置
                if (Array.isArray(fieldName) && fieldName.length === 2) {
                  const [index, fieldKey] = fieldName;
                  
                  // 判断是哪个列表
                  if (allFormData.policy_cases && allFormData.policy_cases[index]) {
                    fileList = allFormData.policy_cases[index][fieldKey] || [];
                  } else if (allFormData.payment_methods && allFormData.payment_methods[index]) {
                    fileList = allFormData.payment_methods[index][fieldKey] || [];
                  } else if (allFormData.external_markets && allFormData.external_markets[index]) {
                    fileList = allFormData.external_markets[index][fieldKey] || [];
                  }
                } else {
                  fileList = form.getFieldValue(fieldName) || [];
                }
              } catch (e) {
                console.warn('获取字段值失败:', e);
                fileList = [];
              }
              
              return renderAttachmentPreview(fileList);
            }}
          </Form.Item>
        </Form.Item>
      );
    } else {
      // 编辑模式：显示上传组件 + 预览
      const token = localStorage.getItem('token');
      
      return (
        <Form.Item {...field} name={fieldName} valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList} style={{ margin: 0 }}>
          <Upload
            action="/api/upload"
            listType="text"
            multiple
            beforeUpload={validateUpload}
            headers={{
              'Authorization': `Bearer ${token}`
            }}
            onChange={(info) => {
              if (info.file.status === 'done') {
                message.success(`${info.file.name} 文件上传成功`);
              } else if (info.file.status === 'error') {
                message.error(`${info.file.name} 文件上传失败`);
              }
            }}
            onError={(error) => {
              console.error('文件上传错误:', error);
            }}
            itemRender={(originNode, file, fileList) => {
              // 自定义文件列表项渲染，包含预览功能
              console.log('=== Upload itemRender ===');
              console.log('file:', {
                uid: file.uid,
                name: file.name,
                filename: file.filename,
                path: file.path,
                url: file.url,
                status: file.status,
                hasResponse: !!file.response,
                response: file.response
              });
              
              let fileName = '未知文件';
              let fileUrl = null;
              
              if (file && file.response && file.response.file) {
                // 新上传的文件
                fileName = file.response.file.originalname || file.name || '未知文件';
                fileUrl = file.response.file.path;
                console.log('新上传文件:', { fileName, fileUrl });
              } else if (file && file.name) {
                // 从数据库加载的文件
                fileName = file.name;
                fileUrl = file.path || file.url;
                console.log('数据库文件:', { fileName, fileUrl });
              }
              
              if (fileUrl && file.status === 'done') {
                // 确保URL格式正确，并对中文字符进行编码
                let fullUrl;
                if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
                  fullUrl = fileUrl;
                } else if (fileUrl.startsWith('/')) {
                  // 对URL路径中的每个部分分别进行编码（保留 / 分隔符）
                  const urlParts = fileUrl.split('/');
                  const encodedParts = urlParts.map(part => encodeURIComponent(part));
                  fullUrl = encodedParts.join('/');
                } else {
                  // 不是根路径，添加 / 前缀并编码
                  const urlParts = fileUrl.split('/');
                  const encodedParts = urlParts.map(part => encodeURIComponent(part));
                  fullUrl = '/' + encodedParts.join('/');
                }
                console.log('最终URL:', fullUrl);
                
                return (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    padding: '4px 0',
                    fontSize: '12px'
                  }}>
                    <a 
                      href={fullUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#1890ff', 
                        textDecoration: 'none',
                        flex: 1
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                      onClick={() => console.log('点击文件链接:', fullUrl)}
                    >
                      📎 {fileName}
                    </a>
                    <Button
                      type="link"
                      size="small"
                      icon={<FullscreenOutlined />}
                      style={{ padding: '0 4px', fontSize: '12px' }}
                      onClick={() => {
                        console.log('全屏查看文件:', fullUrl);
                        setFullscreenModal({
                          visible: true,
                          value: fullUrl,
                          fieldPath: `附件: ${fileName}`,
                          isAttachment: true
                        });
                      }}
                      title="全屏查看"
                    />
                    <Button
                      type="link"
                      size="small"
                      danger
                      onClick={() => {
                        console.log('删除文件:', file.uid);
                        const newFileList = fileList.filter(f => f.uid !== file.uid);
                        form.setFieldValue(fieldName, newFileList);
                      }}
                      style={{ padding: '0 4px', fontSize: '12px' }}
                      title="删除"
                    >
                      ×
                    </Button>
                  </div>
                );
              }
              
              // 对于上传中或失败的文件，使用默认渲染
              console.log('使用默认渲染');
              return originNode;
            }}
          >
            <Button size="small" icon={<UploadOutlined />}>上传文件</Button>
          </Upload>
        </Form.Item>
      );
    }
  };

  const validateUpload = (file) => {
    console.log('=== 前端文件验证开始 ===');
    console.log('文件信息:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });
    
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      console.log('文件大小验证失败:', file.size / 1024 / 1024, 'MB');
      message.error('文件大小不能超过10MB!');
      return false;
    }
    
    const isPDF = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/');
    const isDoc = file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    
    console.log('文件类型检查:', {
      type: file.type,
      isPDF,
      isImage,
      isDoc
    });
    
    if (!isPDF && !isImage && !isDoc) {
      console.log('文件类型验证失败');
      message.error('只能上传PDF文件、图片或文档!');
      return false;
    }
    
    console.log('文件验证通过');
    console.log('=== 前端文件验证结束 ===');
    return true;
  };

  const handleFullscreenEdit = (fieldPath, currentValue) => {
    setFullscreenModal({ visible: true, value: currentValue || '', fieldPath });
  };

  const handleFullscreenSave = () => {
    // 只有在非只读模式下才允许保存
    if (!isFieldReadOnly() && fullscreenModal.fieldPath) {
      form.setFieldValue(fullscreenModal.fieldPath, fullscreenModal.value);
    }
    setFullscreenModal({ visible: false, value: '', fieldPath: null });
  };

  const handleSubmit = async (values) => {
    try {
      console.log('=== handleSubmit 开始 ===');
      console.log('原始 values:', JSON.stringify(values, null, 2));
      
      // 提交前再次验证省级政策权限
      const isProvincialPolicy = !values.responsible_city;
      const isRestrictedProvince = PROVINCE_EDITOR_MAP.hasOwnProperty(values.province);
      
      if (!editingRecord && isProvincialPolicy && isRestrictedProvince) {
        const allowedEditor = PROVINCE_EDITOR_MAP[values.province];
        const isAuthorized = user && (
          user.username === allowedEditor || 
          ['admin', 'super_admin'].includes(user.role)
        );
        
        if (!isAuthorized) {
          message.error(`您无权填写${values.province}的省级政策情况，该省份只能由指定编辑者填写`);
          return;
        }
      }
      
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      // 处理文件数据：只保留必要的文件信息
      const processFileList = (fileList) => {
        console.log('processFileList 输入:', fileList);
        if (!fileList || !Array.isArray(fileList)) return [];
        
        const processed = fileList.map((file, index) => {
          console.log(`处理文件 ${index}:`, {
            uid: file.uid,
            name: file.name,
            filename: file.filename,
            path: file.path,
            url: file.url,
            hasResponse: !!file.response,
            response: file.response
          });
          
          if (file.response && file.response.file) {
            // 新上传的文件
            const result = {
              uid: file.uid,
              name: file.response.file.originalname || file.name,
              filename: file.response.file.filename,
              path: file.response.file.path,
              size: file.response.file.size,
              status: 'done'
            };
            console.log(`文件 ${index} 处理结果（新上传）:`, result);
            return result;
          } else if (file.filename && file.path) {
            // 已存在的文件（从数据库加载的）
            const result = {
              uid: file.uid,
              name: file.name,
              filename: file.filename,
              path: file.path,
              size: file.size,
              status: 'done'
            };
            console.log(`文件 ${index} 处理结果（已存在）:`, result);
            return result;
          }
          console.log(`文件 ${index} 被过滤（不符合条件）`);
          return null;
        }).filter(Boolean);
        
        console.log('processFileList 输出:', processed);
        return processed;
      };

      // 处理嵌套数组中的文件
      const processedValues = {
        ...values,
        policy_cases: values.policy_cases?.map((pc, idx) => {
          console.log(`处理 policy_case ${idx} 的附件:`, pc.attachments);
          return {
            ...pc,
            attachments: processFileList(pc.attachments)
          };
        }) || [],
        payment_methods: values.payment_methods?.map((pm, idx) => {
          console.log(`处理 payment_method ${idx} 的附件:`, pm.attachments);
          return {
            ...pm,
            attachments: processFileList(pm.attachments)
          };
        }) || [],
        external_markets: values.external_markets?.map((em, idx) => {
          console.log(`处理 external_market ${idx} 的附件:`, em.attachments);
          return {
            ...em,
            attachments: processFileList(em.attachments)
          };
        }) || []
      };
      
      console.log('处理后的 processedValues:', JSON.stringify(processedValues, null, 2));
      console.log('处理后的 processedValues:', JSON.stringify(processedValues, null, 2));

      if (editingRecord) {
        // 编辑模式：更新数据
        console.log('执行更新操作，ID:', editingRecord.id);
        const response = await axios.put(`/api/ma-policy/${editingRecord.id}`, processedValues, { headers });
        
        // 处理主责占比警告
        if (response.data.responsibilityRatioWarning) {
          message.warning(response.data.responsibilityRatioWarning);
        } else {
          message.success('数据更新成功');
        }
      } else {
        // 新增模式：创建数据
        console.log('执行创建操作');
        const response = await axios.post('/api/ma-policy', processedValues, { headers });
        
        // 处理主责占比警告
        if (response.data.responsibilityRatioWarning) {
          message.warning(response.data.responsibilityRatioWarning);
        } else {
          message.success('数据创建成功');
        }
        clearDraft(); // 提交成功后清除草稿
      }
      console.log('=== handleSubmit 完成 ===');
      form.resetFields();
      onSuccess();
    } catch (error) {
      console.error('=== handleSubmit 错误 ===', error);
      
      // 特别处理主责占比错误
      if (error.response?.data?.responsibilityRatioError) {
        message.error(error.response.data.message);
      } else {
        message.error((editingRecord ? '更新' : '提交') + '失败：' + (error.response?.data?.message || '未知错误'));
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    // 只有在新增模式且没有暂存过的情况下才清除草稿
    if (!editingRecord && !hasSavedDraft) {
      clearDraft();
    }
    setFullscreenModal({ visible: false, value: '', fieldPath: null });
    onCancel();
  };

  const divisionOptions = [
    { value: '北区', label: '北区' },
    { value: '东区', label: '东区' },
    { value: '南区', label: '南区' },
    { value: '中区', label: '中区' }
  ];

  // 分区-省份映射
  const divisionProvinceMap = {
    '南区': ['广东省', '福建省', '广西', '贵州省', '海南省', '湖南省', '江西省', '四川省', '云南省', '重庆'],
    '北区': ['河北省', '北京', '黑龙江省', '吉林省', '辽宁省', '内蒙', '山西省', '天津'],
    '中区': ['山东省', '甘肃省', '河南省', '湖北省', '宁夏', '青海省', '陕西省', '新疆'],
    '东区': ['安徽省', '江苏省', '上海', '浙江省']
  };

  // 省份-城市映射
  const provinceCityMap = {
    '山东省': ['济南', '青岛', '烟台','威海','日照','潍坊','淄博','东营','滨州'],
    '陕西省': ['西安', '安康','宝鸡','铜川','渭南','榆林'],
    '新疆': ['乌鲁木齐', '阿克苏', '喀什','昌吉','克拉玛依','伊犁'],
    '甘肃省': ['兰州', '天水','酒泉','庆阳','武威','白银','临夏'],
    '河南省': ['平顶山','南阳','濮阳','安阳','开封','信阳','周口','郑州','洛阳','新乡'],
    '湖北省': ['襄阳','十堰','宜昌','荆州','武汉','黄石','黄冈','恩施'],
    '青海省': ['西宁'],
    '宁夏': ['银川'],
    '浙江省': ['杭州', '绍兴', '宁波', '湖州', '嘉兴', '舟山', '台州', '温州', '金华', '丽水', '衢州'],
    '江苏省': ['南京', '苏州', '无锡', '常州', '镇江', '连云港', '淮安', '盐城', '宿迁', '徐州', '泰州', '扬州', '南通'],
    '安徽省': ['合肥', '淮南', '芜湖', '蚌埠', '安庆', '阜阳', '滁州', '宿州', '马鞍山'],
    '河北省': ['石家庄', '保定', '邯郸', '邢台'],
    '黑龙江省': ['哈尔滨', '大庆', '齐齐哈尔', '牡丹江'],
    '内蒙': ['呼和浩特', '包头', '赤峰', '鄂尔多斯'],
    '山西省': ['太原', '大同', '运城', '晋城'],
    '辽宁省': ['沈阳', '大连', '鞍山', '营口'],
    '吉林省': ['长春', '吉林市', '延边州', '松原'],
    '北京': ['海淀', '朝阳', '东城', '西城', '丰台'],
    '广东省': ['广东省医保', '广东省市场管理局', '广东省工信厅', '广东省卫健', '广东省药监局', '潮州市', '汕头市', '汕尾市', '河源市', '清远市', '广州市', '广州市医保', '广州市卫健', '佛山市', '珠海市', '江门市', '阳江市', '东莞市', '肇庆市', '云浮市', '惠州市', '梅州市', '揭阳市', '茂名市', '深圳市', '湛江市', '韶关市', '中山市'],
    '海南省': ['儋州市', '海口市', '三亚市', '省直辖县级行政区划'],
    '广西': ['百色市', '北海市', '防城港市', '贵港市', '桂林市', '河池市', '贺州市', '来宾市', '柳州市', '南宁市', '钦州市', '梧州市', '玉林市'],
    '湖南省': ['常德市', '郴州市', '衡阳市', '怀化市', '娄底市', '邵阳市', '湘潭市', '益阳市', '永州市', '岳阳市', '长沙市', '株洲市'],
    '江西省': ['抚州市', '赣州市', '吉安市', '景德镇市', '九江市', '南昌市', '萍乡市', '上饶市', '新余市', '宜春市', '鹰潭市'],
    '福建省': ['福州市', '龙岩市', '南平市', '宁德市', '莆田市', '泉州市', '三明市', '厦门市', '漳州市'],
    '四川省': ['巴中市', '成都市', '达州市', '德阳市', '广安市', '广元市', '乐山市', '凉山彝族自治州', '泸州市', '眉山市', '绵阳市', '南充市', '内江市', '攀枝花市', '遂宁市', '雅安市', '宜宾市', '资阳市', '自贡市'],
    '贵州省': ['黔东南苗族侗族自治州', '黔南布依族苗族自治州', '黔西南布依族苗族自治州', '遵义市','安顺市', '毕节市', '贵阳市', '六盘水市'],
    '重庆': ['重庆市'],
    '云南省': ['保山市', '楚雄彝族自治州', '红河哈尼族彝族自治州', '昆明市', '普洱市', '曲靖市', '文山壮族苗族自治州', '西双版纳傣族自治州', '玉溪市', '昭通市', '大理白族自治州']
  };

  // 获取当前选择的分区对应的省份列表
  const getProvinceOptions = () => {
    if (selectedDivision && divisionProvinceMap[selectedDivision]) {
      return divisionProvinceMap[selectedDivision].map(p => ({ value: p, label: p }));
    }
    // 返回所有省份
    return [
      { value: '河北省', label: '河北省' }, { value: '山西省', label: '山西省' },
      { value: '辽宁省', label: '辽宁省' }, { value: '吉林省', label: '吉林省' },
      { value: '黑龙江省', label: '黑龙江省' }, { value: '江苏省', label: '江苏省' },
      { value: '浙江省', label: '浙江省' }, { value: '安徽省', label: '安徽省' },
      { value: '福建省', label: '福建省' }, { value: '江西省', label: '江西省' },
      { value: '山东省', label: '山东省' }, { value: '河南省', label: '河南省' },
      { value: '湖北省', label: '湖北省' }, { value: '湖南省', label: '湖南省' },
      { value: '广东省', label: '广东省' }, { value: '海南省', label: '海南省' },
      { value: '四川省', label: '四川省' }, { value: '贵州省', label: '贵州省' },
      { value: '云南省', label: '云南省' }, { value: '陕西省', label: '陕西省' },
      { value: '甘肃省', label: '甘肃省' }, { value: '青海省', label: '青海省' },
      { value: '内蒙', label: '内蒙' }, { value: '广西', label: '广西' },
      { value: '西藏', label: '西藏' }, { value: '宁夏', label: '宁夏' },
      { value: '新疆', label: '新疆' }, { value: '北京', label: '北京' },
      { value: '天津', label: '天津' }, { value: '上海', label: '上海' },
      { value: '重庆', label: '重庆' }
    ];
  };

  // 获取当前选择的省份对应的城市列表
  const getCityOptions = () => {
    if (selectedProvince && provinceCityMap[selectedProvince]) {
      return provinceCityMap[selectedProvince].map(c => ({ value: c, label: c }));
    }
    return [];
  };

  // 判断是否显示主责城市表单
  // - isCityMode为true时显示城市表单
  // - 用户角色为city_editor时，始终显示城市表单
  // - 编辑模式下，如果记录有responsible_city，则显示城市表单
  // - 用户有can_create_city权限时，根据isCityMode决定
  const isCityEditor = user && (user.role === 'city_editor' || user.can_create_city === true || user.can_create_city === 1 || user.can_create_city === '1');
  
  // 关键修复：编辑模式下，根据记录是否有responsible_city来判断
  const hasResponsibleCity = editingRecord && editingRecord.responsible_city;
  const showCityForm = isCityMode || (user && user.role === 'city_editor') || hasResponsibleCity;

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '20px',
    fontSize: '12px'
  };

  const headerCellStyle = {
    backgroundColor: '#b19cd9',
    border: '1px solid #000',
    padding: '8px',
    textAlign: 'center',
    fontWeight: 'bold'
  };

  const cellStyle = {
    border: '1px solid #000',
    padding: '8px',
    textAlign: 'center'
  };

  const productSelectCellStyle = {
    border: '1px solid #000',
    padding: '8px',
    textAlign: 'left',
    minWidth: '120px',
    maxWidth: '150px'
  };

  const pinkHeaderStyle = {
    backgroundColor: '#f8d7da',
    border: '1px solid #000',
    padding: '8px',
    textAlign: 'center',
    fontWeight: 'bold'
  };

  const lightBlueHeaderStyle = {
    backgroundColor: '#d1ecf1',
    border: '1px solid #000',
    padding: '8px',
    textAlign: 'center',
    fontWeight: 'bold'
  };

  // 只读字段样式
  const readOnlyInputStyle = {
    color: '#000000',
    backgroundColor: '#f5f5f5',
    cursor: 'default'
  };

  const readOnlyTextAreaStyle = {
    color: '#000000',
    backgroundColor: '#f5f5f5',
    cursor: 'default',
    resize: 'none'
  };

  return (
    <Modal
      title={<div style={{ textAlign: 'center', fontSize: '20px', fontWeight: 'bold' }}>
        {readOnlyMode ? '查看' : (editingRecord ? '编辑' : '新增')}{showCityForm ? '区域MA主责城市政策情况填写表' : '区域MA省级政策情况填写表'}
      </div>}
      open={visible}
      onCancel={handleCancel}
      footer={readOnlyMode ? [
        <Button key="close" onClick={handleCancel}>
          关闭
        </Button>
      ] : [
        <Button key="cancel" onClick={handleCancel}>
          取消
        </Button>,
        // 只在新增模式下显示暂存按钮
        ...(!editingRecord ? [
          <Button key="draft" onClick={handleSaveDraft}>
            暂存
          </Button>
        ] : []),
        <Button key="submit" type="primary" onClick={() => form.submit()}>
          {editingRecord ? '更新' : '提交'}
        </Button>
      ]}
      width={1000}
      style={{ top: 20 }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {/* 表头 - 基本信息 */}
        <table style={{ ...tableStyle, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ ...headerCellStyle, width: showCityForm ? '20%' : '25%' }}>MA姓名</th>
              <th style={{ ...headerCellStyle, width: showCityForm ? '20%' : '25%' }}>RMA姓名</th>
              <th style={{ ...headerCellStyle, width: showCityForm ? '20%' : '25%' }}>分区</th>
              <th style={{ ...headerCellStyle, width: showCityForm ? '15%' : '20%' }}>省份</th>
              {showCityForm && <th style={{ ...headerCellStyle, width: '15%' }}>主责城市</th>}
              <th style={{ ...headerCellStyle, width: showCityForm ? '10%' : '15%' }}>主责占比</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={cellStyle}>
                <Form.Item name="ma_name" style={{ margin: 0 }} rules={[{ required: true, message: '请填写' }]}>
                  <Input 
                    placeholder="填写" 
                    size="small" 
                    disabled={isFieldReadOnly('ma_name')} 
                    style={isFieldReadOnly('ma_name') ? readOnlyInputStyle : {}}
                  />
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="rma_name" style={{ margin: 0 }} rules={[{ required: true, message: '请填写' }]}>
                  <Input 
                    placeholder="填写" 
                    size="small" 
                    disabled={isFieldReadOnly()} 
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                  />
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="division" style={{ margin: 0 }} rules={[{ required: true, message: '请选择' }]}>
                  <Select 
                    placeholder="单选" 
                    size="small" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    onChange={(value) => {
                      // 分区变化时清空省份和城市，并更新state
                      setSelectedDivision(value);
                      setSelectedProvince(null);
                      form.setFieldsValue({ province: undefined, responsible_city: undefined });
                    }}
                  >
                    {divisionOptions.map(option => (
                      <Option key={option.value} value={option.value}>{option.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="province" style={{ margin: 0 }} rules={[{ required: true, message: '请选择' }]}>
                  <Select 
                    placeholder="单选" 
                    size="small" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    onChange={handleProvinceChange}
                  >
                    {getProvinceOptions().map(option => (
                      <Option key={option.value} value={option.value}>{option.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </td>
              {showCityForm && (
                <td style={cellStyle}>
                  <Form.Item name="responsible_city" style={{ margin: 0 }} rules={[{ required: true, message: '请选择' }]}>
                    <Select 
                      placeholder="单选" 
                      size="small" 
                      disabled={isFieldReadOnly()}
                      style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    >
                      {getCityOptions().map(option => (
                        <Option key={option.value} value={option.value}>{option.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </td>
              )}
              <td style={cellStyle}>
                <Form.Item name="responsibility_ratio" style={{ margin: 0 }}>
                  <InputNumber
                    min={0}
                    max={100}
                    precision={0}
                    size="small"
                    placeholder="0-100"
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : { width: '80px' }}
                    addonAfter="%"
                  />
                </Form.Item>
              </td>
            </tr>
          </tbody>
        </table>

        {/* VBP全流程管理 */}
        <div style={{ marginBottom: '10px' }}>
          <div style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: '10px' }}>VBP全流程管理</div>
          {!isFieldReadOnly() && (
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              {VBP_QUARTER_COPY_MAP.map(({ from, to, label }) => (
                <Button
                  key={`${from}-${to}`}
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyQuarterData(from, to)}
                  title={`一键复制 ${label}`}
                >
                  {label}
                </Button>
              ))}
            </div>
          )}
        </div>
        
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={headerCellStyle} colSpan="11">国采持续相关</th>
            </tr>
            <tr>
              <th style={headerCellStyle} rowSpan="2"></th>
              <th style={headerCellStyle} colSpan="2">25年Q4</th>
              <th style={headerCellStyle} colSpan="2">26年Q1</th>
              <th style={headerCellStyle} colSpan="2">26年Q2</th>
              <th style={headerCellStyle} colSpan="2">26年Q3</th>
              <th style={headerCellStyle} colSpan="2">26年Q4</th>
            </tr>
          </thead>
          <tbody>
            {/* CV领域 - 标签行 */}
            <tr>
              <td style={headerCellStyle} rowSpan="2">CV领域</td>
              <td style={headerCellStyle}>数量比</td>
              <td style={headerCellStyle}>金额比</td>
              <td style={headerCellStyle}>数量比</td>
              <td style={headerCellStyle}>金额比</td>
              <td style={headerCellStyle}>数量比</td>
              <td style={headerCellStyle}>金额比</td>
              <td style={headerCellStyle}>数量比</td>
              <td style={headerCellStyle}>金额比</td>
              <td style={headerCellStyle}>数量比</td>
              <td style={headerCellStyle}>金额比</td>
            </tr>
            {/* CV领域 - 选择框行 */}
            <tr>
              <td style={cellStyle}>
                <Form.Item name="hcv_pi_q4_2025_quantity_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="9:1">9:1</Option>
                    <Option value="8:2">8:2</Option>
                    <Option value="7:3">7:3</Option>
                    <Option value="6:4">6:4</Option>
                    <Option value="5:5">5:5</Option>
                    <Option value="无限制">无限制</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="hcv_pi_q4_2025_amount_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="是">是</Option>
                    <Option value="否">否</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="hcv_pi_q1_2026_quantity_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="9:1">9:1</Option>
                    <Option value="8:2">8:2</Option>
                    <Option value="7:3">7:3</Option>
                    <Option value="6:4">6:4</Option>
                    <Option value="5:5">5:5</Option>
                    <Option value="无限制">无限制</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="hcv_pi_q1_2026_amount_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="是">是</Option>
                    <Option value="否">否</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="hcv_pi_q2_2026_quantity_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="9:1">9:1</Option>
                    <Option value="8:2">8:2</Option>
                    <Option value="7:3">7:3</Option>
                    <Option value="6:4">6:4</Option>
                    <Option value="5:5">5:5</Option>
                    <Option value="无限制">无限制</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="hcv_pi_q2_2026_amount_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="是">是</Option>
                    <Option value="否">否</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="hcv_pi_q3_2026_quantity_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="9:1">9:1</Option>
                    <Option value="8:2">8:2</Option>
                    <Option value="7:3">7:3</Option>
                    <Option value="6:4">6:4</Option>
                    <Option value="5:5">5:5</Option>
                    <Option value="无限制">无限制</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="hcv_pi_q3_2026_amount_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="是">是</Option>
                    <Option value="否">否</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="hcv_pi_q4_2026_quantity_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="9:1">9:1</Option>
                    <Option value="8:2">8:2</Option>
                    <Option value="7:3">7:3</Option>
                    <Option value="6:4">6:4</Option>
                    <Option value="5:5">5:5</Option>
                    <Option value="无限制">无限制</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="hcv_pi_q4_2026_amount_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="是">是</Option>
                    <Option value="否">否</Option>
                  </Select>
                </Form.Item>
              </td>
            </tr>
            {/* 其他领域 - 标签行 */}
            <tr>
              <td style={headerCellStyle} rowSpan="2">其他领域（选填）</td>
              <td style={headerCellStyle}>数量比</td>
              <td style={headerCellStyle}>相关领域</td>
              <td style={headerCellStyle}>数量比</td>
              <td style={headerCellStyle}>相关领域</td>
              <td style={headerCellStyle}>数量比</td>
              <td style={headerCellStyle}>相关领域</td>
              <td style={headerCellStyle}>数量比</td>
              <td style={headerCellStyle}>相关领域</td>
              <td style={headerCellStyle}>数量比</td>
              <td style={headerCellStyle}>相关领域</td>
            </tr>
            {/* 其他领域 - 选择框行 */}
            <tr>
              <td style={cellStyle}>
                <Form.Item name="cns_q4_2025_quantity_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="9:1">9:1</Option>
                    <Option value="8:2">8:2</Option>
                    <Option value="7:3">7:3</Option>
                    <Option value="6:4">6:4</Option>
                    <Option value="5:5">5:5</Option>
                    <Option value="无限制">无限制</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="cns_q4_2025_amount_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="PI">PI</Option>
                    <Option value="CNS">CNS</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="cns_q1_2026_quantity_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="9:1">9:1</Option>
                    <Option value="8:2">8:2</Option>
                    <Option value="7:3">7:3</Option>
                    <Option value="6:4">6:4</Option>
                    <Option value="5:5">5:5</Option>
                    <Option value="无限制">无限制</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="cns_q1_2026_amount_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="PI">PI</Option>
                    <Option value="CNS">CNS</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="cns_q2_2026_quantity_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="9:1">9:1</Option>
                    <Option value="8:2">8:2</Option>
                    <Option value="7:3">7:3</Option>
                    <Option value="6:4">6:4</Option>
                    <Option value="5:5">5:5</Option>
                    <Option value="无限制">无限制</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="cns_q2_2026_amount_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="PI">PI</Option>
                    <Option value="CNS">CNS</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="cns_q3_2026_quantity_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="9:1">9:1</Option>
                    <Option value="8:2">8:2</Option>
                    <Option value="7:3">7:3</Option>
                    <Option value="6:4">6:4</Option>
                    <Option value="5:5">5:5</Option>
                    <Option value="无限制">无限制</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="cns_q3_2026_amount_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="PI">PI</Option>
                    <Option value="CNS">CNS</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="cns_q4_2026_quantity_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="9:1">9:1</Option>
                    <Option value="8:2">8:2</Option>
                    <Option value="7:3">7:3</Option>
                    <Option value="6:4">6:4</Option>
                    <Option value="5:5">5:5</Option>
                    <Option value="无限制">无限制</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="cns_q4_2026_amount_ratio" style={{ margin: 0 }}>
                  <Select 
                    size="small" 
                    placeholder="单选" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="PI">PI</Option>
                    <Option value="CNS">CNS</Option>
                  </Select>
                </Form.Item>
              </td>
            </tr>
          </tbody>
        </table>

        {/* 省联盟扩围相关 - 已隐藏 */}
        {/* 
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={headerCellStyle} colSpan="2">省联盟扩围相关</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={headerCellStyle}>26年是否扩围VBP产品</td>
              <td style={headerCellStyle}>可多华是否纳入省联盟采购</td>
            </tr>
            <tr>
              <td style={cellStyle}>
                <Form.Item name="province_management_2026" style={{ margin: 0 }}>
                  <Select 
                    placeholder="单选" 
                    size="small" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                  >
                    <Option value="是">是</Option>
                    <Option value="否">否</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="province_coordination" style={{ margin: 0 }}>
                  <Select 
                    placeholder="单选" 
                    size="small" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                  >
                    <Option value="是">是</Option>
                    <Option value="否">否</Option>
                  </Select>
                </Form.Item>
              </td>
            </tr>
          </tbody>
        </table>
        */}

        {/* 红黄标管控政策 */}
        <div style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: '10px' }}>红黄标管控政策</div>
        
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...pinkHeaderStyle, width: '50%' }}>标红产品（多选）</th>
              <th style={{ ...pinkHeaderStyle, width: '50%' }}>未被标黄产品（多选）</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={cellStyle}>
                <Form.Item name="marked_red_products" style={{ margin: 0 }}>
                  <Select
                    mode="multiple"
                    placeholder="多选"
                    size="small"
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : { width: '100%' }}
                    className="product-select"
                    onChange={(values) => setMarkedRedProducts(values || [])}
                  >
                    {productOptions.map(product => (
                      <Option 
                        key={product} 
                        value={product}
                        disabled={unmarkedYellowProducts.includes(product)}
                      >
                        {product}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="unmarked_yellow_products" style={{ margin: 0 }}>
                  <Select
                    mode="multiple"
                    placeholder="多选"
                    size="small"
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : { width: '100%' }}
                    className="product-select"
                    onChange={(values) => setUnmarkedYellowProducts(values || [])}
                  >
                    {productOptions.map(product => (
                      <Option 
                        key={product} 
                        value={product}
                        disabled={markedRedProducts.includes(product)}
                      >
                        {product}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </td>
            </tr>
          </tbody>
        </table>
        
        
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={pinkHeaderStyle}>管理方案</th>
              <th style={pinkHeaderStyle}>是否有明确政策发文</th>
              <th style={pinkHeaderStyle}>是否有官方明确澄清<br/><span style={{ fontSize: '10px', fontWeight: 'normal' }}>（不考核分子式）</span></th>
              <th style={pinkHeaderStyle}>是/否改善弹窗警示内容</th>
              <th style={pinkHeaderStyle}>*金额比例（红10%，红黄25%）<br/><span style={{ fontSize: '10px', fontWeight: 'normal' }}>实际：红___%，红黄___%</span></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={cellStyle}>
                <Form.Item name="management_plan" style={{ margin: 0 }}>
                  <Select 
                    placeholder="请选择" 
                    size="small" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                  >
                    <Option value="分子式考核">分子式考核</Option>
                    <Option value="院内总金额">院内总金额</Option>
                    <Option value="不明确">不明确</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="has_policy_document" style={{ margin: 0 }}>
                  <Select 
                    placeholder="请选择" 
                    size="small" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly()}
                    onChange={(value) => setHasPolicyDocument(value)}
                  >
                    <Option value="是">是</Option>
                    <Option value="否">否</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="clarified_total_amount_assessment" style={{ margin: 0 }}>
                  <Select 
                    placeholder="请选择" 
                    size="small" 
                    disabled={isFieldReadOnly() || hasPolicyDocument !== '是'}
                    style={isFieldReadOnly() || hasPolicyDocument !== '是' ? readOnlyInputStyle : {}}
                    allowClear={!isFieldReadOnly() && hasPolicyDocument === '是'}
                  >
                    <Option value="是">是</Option>
                    <Option value="否">否</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <Form.Item name="price_policy_2" style={{ margin: 0 }}>
                  <Select 
                    placeholder="单选" 
                    size="small" 
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyInputStyle : {}}
                  >
                    <Option value="是">是</Option>
                    <Option value="否">否</Option>
                  </Select>
                </Form.Item>
              </td>
              <td style={cellStyle}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>红</span>
                    <Form.Item name="amount_ratio_red" style={{ margin: 0, width: '60px' }}>
                      <InputNumber
                        min={0}
                        max={100}
                        size="small"
                        placeholder="%"
                        disabled={isFieldReadOnly()}
                        style={isFieldReadOnly() ? readOnlyInputStyle : { width: '60px' }}
                      />
                    </Form.Item>
                    <span>%</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>红黄</span>
                    <Form.Item name="amount_ratio_red_yellow" style={{ margin: 0, width: '60px' }}>
                      <InputNumber
                        min={0}
                        max={100}
                        size="small"
                        placeholder="%"
                        disabled={isFieldReadOnly()}
                        style={isFieldReadOnly() ? readOnlyInputStyle : { width: '60px' }}
                      />
                    </Form.Item>
                    <span>%</span>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={pinkHeaderStyle}>补充说明（是否有其他政策突破性的优化考核特例）</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={cellStyle}>
                <Form.Item name="explanation_dimension" style={{ margin: 0 }}>
                  <Input.TextArea
                    rows={3}
                    placeholder="请输入补充说明（最多200字）"
                    maxLength={200}
                    showCount
                    disabled={isFieldReadOnly()}
                    style={isFieldReadOnly() ? readOnlyTextAreaStyle : { resize: 'none' }}
                  />
                </Form.Item>
              </td>
            </tr>
          </tbody>
        </table>

        {/* 其他政策空间 */}
        <div style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: '10px' }}>其他政策空间</div>
        
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={lightBlueHeaderStyle} rowSpan="2">目录准入<br/>- 如：门特门慢等</th>
              <th style={lightBlueHeaderStyle} colSpan="3">内容简述（现状描述、既往政策内容）</th>
              <th style={lightBlueHeaderStyle} rowSpan="2">附件上传<br/><span style={{ fontSize: '10px', fontWeight: 'normal' }}>1.仅限PDF文件，照片<br/>2.可上传多个文件</span></th>
              <th style={lightBlueHeaderStyle} rowSpan="2">RMA赋分</th>
            </tr>
            <tr>
              <th style={lightBlueHeaderStyle}>既往政策内容</th>
              <th style={lightBlueHeaderStyle}>政策更新点</th>
              <th style={lightBlueHeaderStyle}>涉及业务产品</th>
            </tr>
            <tr>
              <th style={lightBlueHeaderStyle}>政策案例1</th>
              <th style={lightBlueHeaderStyle}>文字填写</th>
              <th style={lightBlueHeaderStyle}>文字填写</th>
              <th style={lightBlueHeaderStyle}>多选</th>
              <th style={lightBlueHeaderStyle}>上传</th>
              <th style={lightBlueHeaderStyle}>RMA赋分</th>
            </tr>
          </thead>
          <tbody>
            <Form.List name="policy_cases">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <tr key={field.key}>
                      <td style={cellStyle}>
                        <div style={{ position: 'relative' }}>
                          <Form.Item {...field} name={[field.name, 'case_name']} style={{ margin: 0, marginBottom: '8px' }}>
                            <Input.TextArea 
                              rows={2} 
                              placeholder="点击输入或点击放大图标全屏编辑（最多150字）"
                              style={isFieldReadOnly() ? readOnlyTextAreaStyle : { resize: 'none' }}
                              disabled={isFieldReadOnly()}
                              maxLength={150}
                              showCount
                            />
                          </Form.Item>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minHeight: '24px', justifyContent: 'space-between' }}>
                            <Button 
                              type="link" 
                              size="small"
                              icon={<FullscreenOutlined />}
                              onClick={() => handleFullscreenEdit(['policy_cases', field.name, 'case_name'], form.getFieldValue(['policy_cases', field.name, 'case_name']))}
                              style={{ padding: 0 }}
                            >
                              {isFieldReadOnly() ? '全屏查看' : '全屏编辑'}
                            </Button>
                            {fields.length > 1 && !isFieldReadOnly() && (
                              <Button 
                                type="link" 
                                danger 
                                size="small"
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(field.name)}
                                style={{ padding: 0 }}
                              >
                                删除
                              </Button>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <div style={{ position: 'relative' }}>
                          <Form.Item {...field} name={[field.name, 'past_policy']} style={{ margin: 0, marginBottom: '8px' }}>
                            <Input.TextArea 
                              rows={2} 
                              placeholder="点击输入或点击放大图标全屏编辑（最多150字）"
                              style={isFieldReadOnly() ? readOnlyTextAreaStyle : { resize: 'none' }}
                              disabled={isFieldReadOnly()}
                              maxLength={150}
                              showCount
                            />
                          </Form.Item>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minHeight: '24px' }}>
                            <Button 
                              type="link" 
                              size="small"
                              icon={<FullscreenOutlined />}
                              onClick={() => handleFullscreenEdit(['policy_cases', field.name, 'past_policy'], form.getFieldValue(['policy_cases', field.name, 'past_policy']))}
                              style={{ padding: 0 }}
                            >
                              {isFieldReadOnly() ? '全屏查看' : '全屏编辑'}
                            </Button>
                          </div>
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <div style={{ position: 'relative' }}>
                          <Form.Item {...field} name={[field.name, 'policy_update']} style={{ margin: 0, marginBottom: '8px' }}>
                            <Input.TextArea 
                              rows={2} 
                              placeholder="点击输入或点击放大图标全屏编辑（最多150字）"
                              style={isFieldReadOnly() ? readOnlyTextAreaStyle : { resize: 'none' }}
                              disabled={isFieldReadOnly()}
                              maxLength={150}
                              showCount
                            />
                          </Form.Item>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minHeight: '24px' }}>
                            <Button 
                              type="link" 
                              size="small"
                              icon={<FullscreenOutlined />}
                              onClick={() => handleFullscreenEdit(['policy_cases', field.name, 'policy_update'], form.getFieldValue(['policy_cases', field.name, 'policy_update']))}
                              style={{ padding: 0 }}
                            >
                              {isFieldReadOnly() ? '全屏查看' : '全屏编辑'}
                            </Button>
                          </div>
                        </div>
                      </td>
                      <td style={productSelectCellStyle}>
                        <Form.Item {...field} name={[field.name, 'related_products']} style={{ margin: 0 }}>
                          <Select
                            mode="multiple"
                            placeholder="请选择产品"
                            size="small"
                            className="product-select"
                            style={isFieldReadOnly() ? readOnlyInputStyle : { width: '100%' }}
                            disabled={isFieldReadOnly()}
                            maxTagCount={10}
                            listItemHeight={20}
                            listHeight={200}
                            dropdownStyle={{ maxWidth: 300 }}
                            popupMatchSelectWidth={false}
                            dropdownRender={(menu) => renderSelectAllDropdown(menu, ['policy_cases', field.name, 'related_products'])}
                          >
                            {productOptions.map(product => (
                              <Option key={product} value={product}>{product}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </td>
                      <td style={cellStyle}>
                        {renderAttachmentField(field, [field.name, 'attachments'])}
                      </td>
                      <td style={cellStyle}>
                        {renderRmaScoreField(field, [field.name, 'rma_score'])}
                      </td>
                    </tr>
                  ))}
                  {!isFieldReadOnly() && (
                    <tr>
                      <td colSpan="6" style={{ ...cellStyle, textAlign: 'center', padding: '10px' }}>
                        <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                          新增一行
                        </Button>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </Form.List>
          </tbody>
        </table>

        {/* 支付方式部分 */}
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={lightBlueHeaderStyle} rowSpan="2">支付方式<br/>- 如：URP、APG、DRG&DIP等</th>
              <th style={lightBlueHeaderStyle} colSpan="3">内容简述（现状描述、既往政策内容）</th>
              <th style={lightBlueHeaderStyle} rowSpan="2">附件上传<br/><span style={{ fontSize: '10px', fontWeight: 'normal' }}>1.仅限PDF文件，照片<br/>2.可上传多个文件</span></th>
              <th style={lightBlueHeaderStyle} rowSpan="2">RMA赋分</th>
            </tr>
            <tr>
              <th style={lightBlueHeaderStyle}>既往政策内容</th>
              <th style={lightBlueHeaderStyle}>政策更新点</th>
              <th style={lightBlueHeaderStyle}>涉及业务产品</th>
            </tr>
            <tr>
              <th style={lightBlueHeaderStyle}>政策案例1</th>
              <th style={lightBlueHeaderStyle}>文字填写</th>
              <th style={lightBlueHeaderStyle}>文字填写</th>
              <th style={lightBlueHeaderStyle}>多选</th>
              <th style={lightBlueHeaderStyle}>上传</th>
              <th style={lightBlueHeaderStyle}>RMA赋分</th>
            </tr>
          </thead>
          <tbody>
            <Form.List name="payment_methods">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <tr key={field.key}>
                      <td style={cellStyle}>
                        <div style={{ position: 'relative' }}>
                          <Form.Item {...field} name={[field.name, 'case_name']} style={{ margin: 0, marginBottom: '8px' }}>
                            <Input.TextArea 
                              rows={2} 
                              placeholder="点击输入或点击放大图标全屏编辑（最多150字）"
                              style={isFieldReadOnly() ? readOnlyTextAreaStyle : { resize: 'none' }}
                              disabled={isFieldReadOnly()}
                              maxLength={150}
                              showCount
                            />
                          </Form.Item>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minHeight: '24px', justifyContent: 'space-between' }}>
                            <Button 
                              type="link" 
                              size="small"
                              icon={<FullscreenOutlined />}
                              onClick={() => handleFullscreenEdit(['payment_methods', field.name, 'case_name'], form.getFieldValue(['payment_methods', field.name, 'case_name']))}
                              style={{ padding: 0 }}
                            >
                              {isFieldReadOnly() ? '全屏查看' : '全屏编辑'}
                            </Button>
                            {fields.length > 1 && !isFieldReadOnly() && (
                              <Button 
                                type="link" 
                                danger 
                                size="small"
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(field.name)}
                                style={{ padding: 0 }}
                              >
                                删除
                              </Button>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <div style={{ position: 'relative' }}>
                          <Form.Item {...field} name={[field.name, 'past_policy']} style={{ margin: 0, marginBottom: '8px' }}>
                            <Input.TextArea 
                              rows={2} 
                              placeholder="点击输入或点击放大图标全屏编辑（最多150字）"
                              style={isFieldReadOnly() ? readOnlyTextAreaStyle : { resize: 'none' }}
                              disabled={isFieldReadOnly()}
                              maxLength={150}
                              showCount
                            />
                          </Form.Item>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minHeight: '24px' }}>
                            <Button 
                              type="link" 
                              size="small"
                              icon={<FullscreenOutlined />}
                              onClick={() => handleFullscreenEdit(['payment_methods', field.name, 'past_policy'], form.getFieldValue(['payment_methods', field.name, 'past_policy']))}
                              style={{ padding: 0 }}
                            >
                              {isFieldReadOnly() ? '全屏查看' : '全屏编辑'}
                            </Button>
                          </div>
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <div style={{ position: 'relative' }}>
                          <Form.Item {...field} name={[field.name, 'policy_update']} style={{ margin: 0, marginBottom: '8px' }}>
                            <Input.TextArea 
                              rows={2} 
                              placeholder="点击输入或点击放大图标全屏编辑（最多150字）"
                              style={isFieldReadOnly() ? readOnlyTextAreaStyle : { resize: 'none' }}
                              disabled={isFieldReadOnly()}
                              maxLength={150}
                              showCount
                            />
                          </Form.Item>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minHeight: '24px' }}>
                            <Button 
                              type="link" 
                              size="small"
                              icon={<FullscreenOutlined />}
                              onClick={() => handleFullscreenEdit(['payment_methods', field.name, 'policy_update'], form.getFieldValue(['payment_methods', field.name, 'policy_update']))}
                              style={{ padding: 0 }}
                            >
                              {isFieldReadOnly() ? '全屏查看' : '全屏编辑'}
                            </Button>
                          </div>
                        </div>
                      </td>
                      <td style={productSelectCellStyle}>
                        <Form.Item {...field} name={[field.name, 'related_products']} style={{ margin: 0 }}>
                          <Select
                            mode="multiple"
                            placeholder="请选择产品"
                            size="small"
                            className="product-select"
                            style={isFieldReadOnly() ? readOnlyInputStyle : { width: '100%' }}
                            disabled={isFieldReadOnly()}
                            maxTagCount={10}
                            listItemHeight={20}
                            listHeight={200}
                            dropdownStyle={{ maxWidth: 300 }}
                            popupMatchSelectWidth={false}
                            dropdownRender={(menu) => renderSelectAllDropdown(menu, ['payment_methods', field.name, 'related_products'])}
                          >
                            {productOptions.map(product => (
                              <Option key={product} value={product}>{product}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </td>
                      <td style={cellStyle}>
                        {renderAttachmentField(field, [field.name, 'attachments'])}
                      </td>
                      <td style={cellStyle}>
                        {renderRmaScoreField(field, [field.name, 'rma_score'])}
                      </td>
                    </tr>
                  ))}
                  {!isFieldReadOnly() && (
                    <tr>
                      <td colSpan="6" style={{ ...cellStyle, textAlign: 'center', padding: '10px' }}>
                        <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                          新增一行
                        </Button>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </Form.List>
          </tbody>
        </table>

        {/* 院外市场部分 */}
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={lightBlueHeaderStyle} rowSpan="2">院外市场<br/>- 如：双通道目录等</th>
              <th style={lightBlueHeaderStyle} colSpan="3">内容简述（现状描述、既往政策内容）</th>
              <th style={lightBlueHeaderStyle} rowSpan="2">附件上传<br/><span style={{ fontSize: '10px', fontWeight: 'normal' }}>1.仅限PDF文件，照片<br/>2.可上传多个文件</span></th>
              <th style={lightBlueHeaderStyle} rowSpan="2">RMA赋分</th>
            </tr>
            <tr>
              <th style={lightBlueHeaderStyle}>既往政策内容</th>
              <th style={lightBlueHeaderStyle}>政策更新点</th>
              <th style={lightBlueHeaderStyle}>涉及业务产品</th>
            </tr>
            <tr>
              <th style={lightBlueHeaderStyle}>政策案例1</th>
              <th style={lightBlueHeaderStyle}>文字填写</th>
              <th style={lightBlueHeaderStyle}>文字填写</th>
              <th style={lightBlueHeaderStyle}>多选</th>
              <th style={lightBlueHeaderStyle}>上传</th>
              <th style={lightBlueHeaderStyle}>RMA赋分</th>
            </tr>
          </thead>
          <tbody>
            <Form.List name="external_markets">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <tr key={field.key}>
                      <td style={cellStyle}>
                        <div style={{ position: 'relative' }}>
                          <Form.Item {...field} name={[field.name, 'case_name']} style={{ margin: 0, marginBottom: '8px' }}>
                            <Input.TextArea 
                              rows={2} 
                              placeholder="点击输入或点击放大图标全屏编辑（最多150字）"
                              style={isFieldReadOnly() ? readOnlyTextAreaStyle : { resize: 'none' }}
                              disabled={isFieldReadOnly()}
                              maxLength={150}
                              showCount
                            />
                          </Form.Item>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minHeight: '24px', justifyContent: 'space-between' }}>
                            <Button 
                              type="link" 
                              size="small"
                              icon={<FullscreenOutlined />}
                              onClick={() => handleFullscreenEdit(['external_markets', field.name, 'case_name'], form.getFieldValue(['external_markets', field.name, 'case_name']))}
                              style={{ padding: 0 }}
                            >
                              {isFieldReadOnly() ? '全屏查看' : '全屏编辑'}
                            </Button>
                            {fields.length > 1 && !isFieldReadOnly() && (
                              <Button 
                                type="link" 
                                danger 
                                size="small"
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(field.name)}
                                style={{ padding: 0 }}
                              >
                                删除
                              </Button>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <div style={{ position: 'relative' }}>
                          <Form.Item {...field} name={[field.name, 'past_policy']} style={{ margin: 0, marginBottom: '8px' }}>
                            <Input.TextArea 
                              rows={2} 
                              placeholder="点击输入或点击放大图标全屏编辑（最多150字）"
                              style={isFieldReadOnly() ? readOnlyTextAreaStyle : { resize: 'none' }}
                              disabled={isFieldReadOnly()}
                              maxLength={150}
                              showCount
                            />
                          </Form.Item>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minHeight: '24px' }}>
                            <Button 
                              type="link" 
                              size="small"
                              icon={<FullscreenOutlined />}
                              onClick={() => handleFullscreenEdit(['external_markets', field.name, 'past_policy'], form.getFieldValue(['external_markets', field.name, 'past_policy']))}
                              style={{ padding: 0 }}
                            >
                              {isFieldReadOnly() ? '全屏查看' : '全屏编辑'}
                            </Button>
                          </div>
                        </div>
                      </td>
                      <td style={cellStyle}>
                        <div style={{ position: 'relative' }}>
                          <Form.Item {...field} name={[field.name, 'policy_update']} style={{ margin: 0, marginBottom: '8px' }}>
                            <Input.TextArea 
                              rows={2} 
                              placeholder="点击输入或点击放大图标全屏编辑（最多150字）"
                              style={isFieldReadOnly() ? readOnlyTextAreaStyle : { resize: 'none' }}
                              disabled={isFieldReadOnly()}
                              maxLength={150}
                              showCount
                            />
                          </Form.Item>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minHeight: '24px' }}>
                            <Button 
                              type="link" 
                              size="small"
                              icon={<FullscreenOutlined />}
                              onClick={() => handleFullscreenEdit(['external_markets', field.name, 'policy_update'], form.getFieldValue(['external_markets', field.name, 'policy_update']))}
                              style={{ padding: 0 }}
                            >
                              {isFieldReadOnly() ? '全屏查看' : '全屏编辑'}
                            </Button>
                          </div>
                        </div>
                      </td>
                      <td style={productSelectCellStyle}>
                        <Form.Item {...field} name={[field.name, 'related_products']} style={{ margin: 0 }}>
                          <Select
                            mode="multiple"
                            placeholder="请选择产品"
                            size="small"
                            className="product-select"
                            style={isFieldReadOnly() ? readOnlyInputStyle : { width: '100%' }}
                            disabled={isFieldReadOnly()}
                            maxTagCount={10}
                            listItemHeight={20}
                            listHeight={200}
                            dropdownStyle={{ maxWidth: 300 }}
                            popupMatchSelectWidth={false}
                            dropdownRender={(menu) => renderSelectAllDropdown(menu, ['external_markets', field.name, 'related_products'])}
                          >
                            {productOptions.map(product => (
                              <Option key={product} value={product}>{product}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </td>
                      <td style={cellStyle}>
                        {renderAttachmentField(field, [field.name, 'attachments'])}
                      </td>
                      <td style={cellStyle}>
                        {renderRmaScoreField(field, [field.name, 'rma_score'])}
                      </td>
                    </tr>
                  ))}
                  {!isFieldReadOnly() && (
                    <tr>
                      <td colSpan="6" style={{ ...cellStyle, textAlign: 'center', padding: '10px' }}>
                        <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                          新增一行
                        </Button>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </Form.List>
          </tbody>
        </table>
      </Form>

      {/* 全屏编辑Modal */}
      <Modal
        title={fullscreenModal.isAttachment ? "全屏查看附件" : (isFieldReadOnly() ? "全屏查看" : "全屏编辑")}
        open={fullscreenModal.visible}
        onCancel={() => setFullscreenModal({ visible: false, value: '', fieldPath: null })}
        onOk={isFieldReadOnly() ? undefined : handleFullscreenSave}
        width="90%"
        style={{ top: 20 }}
        okText={isFieldReadOnly() ? undefined : "保存"}
        cancelText={isFieldReadOnly() ? "关闭" : "取消"}
        footer={isFieldReadOnly() ? [
          <Button key="close" onClick={() => setFullscreenModal({ visible: false, value: '', fieldPath: null })}>
            关闭
          </Button>
        ] : [
          <Button key="cancel" onClick={() => setFullscreenModal({ visible: false, value: '', fieldPath: null })}>
            取消
          </Button>,
          <Button key="save" type="primary" onClick={handleFullscreenSave}>
            保存
          </Button>
        ]}
      >
        {fullscreenModal.isAttachment ? (
          <div style={{ textAlign: 'center', height: '70vh' }}>
            <h4 style={{ marginBottom: '16px' }}>{fullscreenModal.fieldPath}</h4>
            <iframe
              src={fullscreenModal.value}
              style={{
                width: '100%',
                height: '100%',
                border: '1px solid #d9d9d9',
                borderRadius: '6px'
              }}
              title="附件预览"
            />
            <div style={{ marginTop: '16px' }}>
              <Button type="primary" onClick={() => window.open(fullscreenModal.value, '_blank')}>
                在新窗口中打开
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ position: 'relative', paddingBottom: '30px' }}>
            <TextArea
              value={fullscreenModal.value}
              onChange={isFieldReadOnly() ? undefined : (e) => {
                // 检查是否是需要限制字符数的字段
                const isLimitedField = fullscreenModal.fieldPath && 
                  Array.isArray(fullscreenModal.fieldPath) && 
                  fullscreenModal.fieldPath.length >= 3 &&
                  ['policy_cases', 'payment_methods', 'external_markets'].includes(fullscreenModal.fieldPath[0]) &&
                  ['case_name', 'past_policy', 'policy_update'].includes(fullscreenModal.fieldPath[2]);
                
                const newValue = e.target.value;
                
                // 如果是限制字段且超过150字，则截断
                if (isLimitedField && newValue.length > 150) {
                  return; // 不更新值，保持原值
                }
                
                setFullscreenModal({ ...fullscreenModal, value: newValue });
              }}
              rows={22}
              placeholder={isFieldReadOnly() ? "" : (() => {
                // 检查是否是需要限制字符数的字段
                const isLimitedField = fullscreenModal.fieldPath && 
                  Array.isArray(fullscreenModal.fieldPath) && 
                  fullscreenModal.fieldPath.length >= 3 &&
                  ['policy_cases', 'payment_methods', 'external_markets'].includes(fullscreenModal.fieldPath[0]) &&
                  ['case_name', 'past_policy', 'policy_update'].includes(fullscreenModal.fieldPath[2]);
                
                return isLimitedField ? "请输入内容...（最多150字）" : "请输入内容...";
              })()}
              style={{ 
                fontSize: '14px',
                marginBottom: '30px'
              }}
              readOnly={isFieldReadOnly()}
              maxLength={(() => {
                // 检查是否是需要限制字符数的字段
                const isLimitedField = fullscreenModal.fieldPath && 
                  Array.isArray(fullscreenModal.fieldPath) && 
                  fullscreenModal.fieldPath.length >= 3 &&
                  ['policy_cases', 'payment_methods', 'external_markets'].includes(fullscreenModal.fieldPath[0]) &&
                  ['case_name', 'past_policy', 'policy_update'].includes(fullscreenModal.fieldPath[2]);
                
                return isLimitedField ? 150 : undefined;
              })()}
              showCount={(() => {
                // 检查是否是需要限制字符数的字段
                const isLimitedField = fullscreenModal.fieldPath && 
                  Array.isArray(fullscreenModal.fieldPath) && 
                  fullscreenModal.fieldPath.length >= 3 &&
                  ['policy_cases', 'payment_methods', 'external_markets'].includes(fullscreenModal.fieldPath[0]) &&
                  ['case_name', 'past_policy', 'policy_update'].includes(fullscreenModal.fieldPath[2]);
                
                return isLimitedField;
              })()}
            />
          </div>
        )}
      </Modal>
    </Modal>
  );
};

export default MAPolicyForm;
